// ============================================================
// useHandouts (v8.363.9) - what the GM shows, and what I am shown.
//
// The GM's watch is the whole collection (drafts included); a player's is
// the audience query. Posting a file is a sequence with a clear order: the
// kind, then the downscale, then the size and the quota judged on what will
// UPLOAD (a 15 MB photo goes up at about 1 MB), then the DRAFT document (the
// rules on both backends refuse a file on a document that does not exist),
// then the file and its thumbnail through the files port, then the refs and
// sizes merged in. A refusal therefore writes nothing; a failure between the
// draft and the refs leaves a draft with no file - visible to the GM as
// such, removable, never shown to a player. The sequence itself is
// `handoutFiles` (v11.82.0), shared with the library's copy into a session.
// ============================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { randomToken } from '../utils/entropy';
import { downscaleImage, makeThumb } from '../utils/imageDownscale';
import {
  emptyHandout,
  endPresentPatch,
  fromDoc,
  presentPatch,
  revealPatch,
  toDoc,
  usage as usageOf,
  handoutFromEntry,
  type BatchItem,
  type BatchResult,
  type Handout,
  type HandoutDoc,
  type HandoutFileEntry,
  type RevealTarget,
  type Refused,
} from '../engine/handouts';
import { metaOf, postHandoutFile, type HandoutsMp, type PostMeta, type Posted } from './handoutFiles';
import type { ChatChannel } from '../engine/chat';
import type { CollectionRead } from '../backend/types';

interface Params {
  sessionId: string | null;
  me: string;
  isGm: boolean;
  mp: HandoutsMp;
  /** Called after a reveal when the GM asked to announce it; the vox line is the caller's. */
  announce?: (h: Handout, audience: string[]) => Promise<void>;
  mint?: () => string;
  now?: () => number;
  /** The image pipeline, injectable for tests (jsdom has no canvas). */
  scale?: typeof downscaleImage;
  thumb?: typeof makeThumb;
}

/** A fresh handout id: time-ordered, with entropy for two in one tick. */
const defaultMint = () => `ho-${Date.now().toString(36)}-${randomToken(4)}`;

/** What every writer needs: where, who, and the clock and id source. */
interface HandoutCtx {
  sessionId: string | null;
  isGm: boolean;
  me: string;
  mp: HandoutsMp;
  mint: () => string;
  now: () => number;
}

export default function useHandouts({
  sessionId,
  me,
  isGm,
  mp,
  announce,
  mint = defaultMint,
  now = Date.now,
  scale = downscaleImage,
  thumb = makeThumb,
}: Params) {
  const handouts = useHandoutFeed(sessionId, me, isGm, mp);
  const [busy, setBusy] = useState<string | null>(null);
  const usage = useMemo(() => usageOf(handouts), [handouts]);
  const ctx = { sessionId, isGm, me, mp, mint, now };
  const { write, postNote, postLink } = useHandoutPosts(ctx);
  const importEntries = useImportEntries(ctx, write);
  const { postFile, postFiles } = usePostFiles({ ...ctx, handouts, setBusy, scale, thumb });
  const edits = useHandoutEdits({ ...ctx, handouts, announce });
  // `edits`: update, reveal, present, endPresent, remove, removeMany.
  return { handouts, usage, busy, postNote, postLink, postFile, postFiles, ...edits, importEntries };
}

const newestFirst = (read: CollectionRead<Record<string, unknown>>) =>
  read.entries
    .map((e) => fromDoc(e.id, e.data as Partial<HandoutDoc>))
    .sort((a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id));

/** The GM's watch is the whole collection (drafts included); a player's is the audience query. */
function useHandoutFeed(sessionId: string | null, me: string, isGm: boolean, mp: HandoutsMp) {
  const [handouts, setHandouts] = useState<Handout[]>([]);
  useEffect(() => {
    if (!sessionId) {
      setHandouts([]);
      return;
    }
    return mp.onHandoutsUpdate(sessionId, isGm ? null : me, (read) => setHandouts(newestFirst(read)));
  }, [sessionId, me, isGm, mp]);
  return handouts;
}

/** A note or a link, as a draft. */
function noteHandout(base: Handout, meta: PostMeta & { notes: string }): Handout {
  return { ...base, ...metaOf(meta), notes: meta.notes, size: meta.notes.length };
}

function linkHandout(base: Handout, meta: PostMeta & { url: string }): Handout {
  return { ...base, ...metaOf(meta), url: meta.url.trim(), title: meta.title?.trim() || meta.url.trim() };
}

/** The GM's posts that need no file: notes and links, as drafts. */
function useHandoutPosts({ sessionId, isGm, me, mp, mint, now }: HandoutCtx) {
  const write = useCallback(
    async (h: Handout) => {
      if (sessionId) await mp.setHandout(sessionId, h.id, toDoc(h));
    },
    [sessionId, mp],
  );
  /** Post a draft built from a fresh base; null when this seat may not post. */
  const post = useCallback(
    async (kind: 'note' | 'link', build: (base: Handout) => Handout): Promise<string | null> => {
      if (!sessionId || !isGm) return null;
      const h = build(emptyHandout(mint(), kind, me, now()));
      await write(h);
      return h.id;
    },
    [sessionId, isGm, me, mint, now, write],
  );
  const postNote = useCallback(
    (meta: PostMeta & { notes: string }) => post('note', (b) => noteHandout(b, meta)),
    [post],
  );
  const postLink = useCallback((meta: PostMeta & { url: string }) => post('link', (b) => linkHandout(b, meta)), [post]);
  return { write, postNote, postLink };
}

/** The session file's section, as drafts: nothing is shown until the GM reveals it again. */
function useImportEntries({ sessionId, isGm, me, mint, now }: HandoutCtx, write: (h: Handout) => Promise<void>) {
  return useCallback(
    async (entries: readonly HandoutFileEntry[]) => {
      if (!sessionId || !isGm) return 0;
      for (const e of entries) await write(handoutFromEntry(e, mint(), me, now()));
      return entries.length;
    },
    [sessionId, isGm, me, mint, now, write],
  );
}

interface PostFileDeps extends HandoutCtx {
  handouts: Handout[];
  setBusy: (id: string | null) => void;
  scale: NonNullable<Params['scale']>;
  thumb: NonNullable<Params['thumb']>;
}

/**
 * Post a file, in the order the header gives: the kind, the downscale,
 * the size and quota on what will upload, the DRAFT document, then the
 * upload. A refusal writes nothing; a failure after the draft rejects.
 */
function usePostOne({ sessionId, isGm, me, mp, mint, now, handouts, setBusy, scale, thumb }: PostFileDeps) {
  return useCallback(
    async (file: File, meta: PostMeta, charged: number): Promise<Posted> => {
      if (!sessionId || !isGm) throw new Error('Only the GM posts handouts.');
      const id = mint();
      setBusy(id);
      try {
        return await postHandoutFile({ mp, sessionId, me, now, scale, thumb }, { id, file, meta, handouts, charged });
      } finally {
        setBusy(null);
      }
    },
    [sessionId, isGm, handouts, me, mint, now, scale, thumb, mp, setBusy],
  );
}

/** A file the server (or the network) refused mid-post, listed with its message. */
function failed(name: string, e: unknown): Refused {
  const sentence = e instanceof Error ? e.message : String(e);
  return { name, reason: sentence.replace(/\.$/, ''), sentence };
}

/**
 * `postFile` resolves with the id or rejects with the sentence to show.
 * `postFiles` posts every file that fits, in order, and returns the ones
 * it did not take with why - one refusal never stops the rest.
 */
function usePostFiles(deps: PostFileDeps) {
  const postOne = usePostOne(deps);
  const postFile = useCallback(
    async (file: File, meta: PostMeta = {}): Promise<string> => {
      const out = await postOne(file, meta, 0);
      if ('refused' in out) throw new Error(out.refused.sentence);
      return out.id;
    },
    [postOne],
  );
  const postFiles = useCallback((items: readonly BatchItem[]) => postBatch(postOne, items), [postOne]);
  return { postFile, postFiles };
}

/** Each item in turn, charging the session for what landed; a throw is listed, never fatal. */
async function postBatch(postOne: ReturnType<typeof usePostOne>, items: readonly BatchItem[]): Promise<BatchResult> {
  const refused: Refused[] = [];
  let charged = 0;
  for (const { file, folder } of items) {
    const out = await postOne(file, { folder }, charged).catch((e: unknown) => ({ refused: failed(file.name, e) }));
    if ('refused' in out) refused.push(out.refused);
    else charged += out.bytes;
  }
  return { total: items.length, refused };
}

type MetaPatch = Partial<Pick<Handout, 'title' | 'folder' | 'tags' | 'notes' | 'url'>>;

/**
 * The merge an edit writes. A merge, never a full write: the file refs
 * live on the document and a replace on Firestore would drop them
 * (PocketBase would not - the two backends differ exactly here).
 */
function metaMerge(cur: Handout, patch: MetaPatch, at: number) {
  const next = { ...cur, ...patch, tags: [...new Set(patch.tags ?? cur.tags)], updatedAt: at };
  const { kind, title, folder, tags, notes, url, updatedAt } = toDoc(next);
  const size = kind === 'note' ? notes.length : next.size;
  return { title, folder, tags, notes, url, size, updatedAt };
}

interface EditDeps extends HandoutCtx {
  handouts: Handout[];
  announce?: Params['announce'];
}

/** A handout this seat may edit, with the session it lives in. */
type Editable = (id: string) => { sid: string; cur: Handout } | null;

/** The GM's edits to a posted handout: metadata, who sees it, and removal. Each is a no-op for a player. */
function useHandoutEdits({ sessionId, isGm, mp, now, handouts, announce }: EditDeps) {
  const editable: Editable = useCallback(
    (id: string) => {
      const cur = handouts.find((h) => h.id === id);
      return sessionId && isGm && cur ? { sid: sessionId, cur } : null;
    },
    [sessionId, isGm, handouts],
  );
  const update = useCallback(
    async (id: string, patch: MetaPatch) => {
      const e = editable(id);
      if (e) await mp.setHandout(e.sid, id, metaMerge(e.cur, patch, now()), true);
    },
    [editable, now, mp],
  );
  const takeDown = useTakeDown(handouts, mp, now);
  const reveal = useReveal(editable, mp, now, announce, takeDown);
  const { present, endPresent } = usePresent(editable, mp, now, takeDown);
  const { remove, removeMany } = useHandoutRemoval(mp, editable);
  return { update, reveal, present, endPresent, remove, removeMany };
}

/**
 * Take down whatever is up besides `id` (v9.8.0): one thing is presented at
 * a time, or ending the newest would put the one before it back on every screen.
 */
function useTakeDown(handouts: Handout[], mp: HandoutsMp, now: () => number) {
  return useCallback(
    async (sid: string, id: string) => {
      const up = handouts.filter((h) => h.id !== id && h.presentedAt > 0);
      for (const h of up) await mp.setHandout(sid, h.id, endPresentPatch(now()), true);
    },
    [handouts, mp, now],
  );
}

type TakeDown = ReturnType<typeof useTakeDown>;

/** Put a shown handout up on every screen in its audience, and take it down (engine/handouts/present). */
function usePresent(editable: Editable, mp: HandoutsMp, now: () => number, takeDown: TakeDown) {
  const present = useCallback(
    async (id: string) => {
      const e = editable(id);
      // A draft has nobody to present to: the card opens the reveal dialog instead.
      if (!e || e.cur.audience.length === 0) return;
      await takeDown(e.sid, id);
      await mp.setHandout(e.sid, id, presentPatch(now()), true);
    },
    [editable, mp, now, takeDown],
  );
  const endPresent = useCallback(
    async (id: string) => {
      const e = editable(id);
      if (e) await mp.setHandout(e.sid, id, endPresentPatch(now()), true);
    },
    [editable, mp, now],
  );
  return { present, endPresent };
}

interface RevealOpts {
  /** Post the vox line that carries it. */
  announce?: boolean;
  /** Put it up on their screens in the same write (v9.8.0). */
  present?: boolean;
}

/**
 * Change who sees a handout; announce it on the vox when asked and somebody
 * can now see it. With `present` the audience and the stamp are ONE write:
 * two would let the presentation be judged against the draft it still was.
 */
function useReveal(
  editable: Editable,
  mp: HandoutsMp,
  now: () => number,
  announce: Params['announce'],
  takeDown: TakeDown,
) {
  return useCallback(
    async (id: string, target: RevealTarget, channels: readonly ChatChannel[], opts: RevealOpts = {}) => {
      const e = editable(id);
      if (!e) return;
      const patch = revealWrite(target, channels, now(), !!opts.present);
      if ('presentedAt' in patch && patch.presentedAt) await takeDown(e.sid, id);
      await mp.setHandout(e.sid, id, patch, true);
      if (opts.announce && patch.audience.length && announce) await announce({ ...e.cur, ...patch }, patch.audience);
    },
    [editable, now, mp, announce, takeDown],
  );
}

/** The reveal's merge, with the presentation's stamp when it is asked for and somebody can see it. */
function revealWrite(target: RevealTarget, channels: readonly ChatChannel[], at: number, present: boolean) {
  const shown = revealPatch(target, channels, at);
  return present && shown.audience.length > 0 ? { ...shown, ...presentPatch(at) } : shown;
}

/** A handout's stored file and thumbnail, whichever it has. */
async function deleteFiles(mp: HandoutsMp, sid: string, h: Handout) {
  if (h.file) await mp.deleteHandoutFile(sid, h.id, 'file', h.file);
  if (h.thumb) await mp.deleteHandoutFile(sid, h.id, 'thumb', h.thumb);
}

/** Files first, then the document: a document without files is a draft, files without a document are garbage. */
function useHandoutRemoval(mp: HandoutsMp, editable: Editable) {
  const remove = useCallback(
    async (id: string) => {
      const e = editable(id);
      if (!e) return;
      await deleteFiles(mp, e.sid, e.cur);
      await mp.deleteHandout(e.sid, id);
    },
    [editable, mp],
  );
  /** One at a time, files then documents, so a refusal midway leaves nothing half-deleted. */
  const removeMany = useCallback(
    async (ids: readonly string[]) => {
      for (const id of ids) await remove(id);
    },
    [remove],
  );
  return { remove, removeMany };
}
